import PropTypes from 'prop-types';
import { useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import { useSnackbar } from '../snackbar';
import { getMathExpressionPreview } from '../../utils/mathExpressionPreview';

const EXPRESSION_CHAR_REGEX =
  /[0-9A-Za-zπΠ\uFF01-\uFF5E.,+\-*/^%()×÷\u2212\u2013\u2014\u2010\u2011\uFE63\uFF0C\u3000\s]/;

const isDigitChar = (char) =>
  (char >= '0' && char <= '9') || (char >= '０' && char <= '９');

const normalizeMathChar = (char) => {
  if (!char) {
    return '';
  }
  const code = char.charCodeAt(0);
  if (code >= 0xff01 && code <= 0xff5e) {
    return String.fromCharCode(code - 0xfee0);
  }
  if (char === '×' || char === '✕') {
    return '*';
  }
  if (char === '÷') {
    return '/';
  }
  if (char === '（') {
    return '(';
  }
  if (char === '）') {
    return ')';
  }
  if (char === '。') {
    return '.';
  }
  if (char === '，') {
    return ',';
  }
  if (
    char === '\u2212' ||
    char === '\u2013' ||
    char === '\u2014' ||
    char === '\u2010' ||
    char === '\u2011' ||
    char === '\uFE63'
  ) {
    return '-';
  }
  return char;
};

const isMathTokenChar = (char) => {
  if (!char) {
    return false;
  }
  const normalized = normalizeMathChar(char);
  if (isDigitChar(normalized)) {
    return true;
  }
  if (
    (normalized >= 'A' && normalized <= 'Z') ||
    (normalized >= 'a' && normalized <= 'z') ||
    normalized === 'π' ||
    normalized === 'Π'
  ) {
    return true;
  }
  return (
    normalized === '.' ||
    normalized === ',' ||
    normalized === '+' ||
    normalized === '-' ||
    normalized === '*' ||
    normalized === '/' ||
    normalized === '^' ||
    normalized === '%' ||
    normalized === '(' ||
    normalized === ')'
  );
};

const splitOnNumericWhitespace = (segment) => {
  if (!segment) {
    return '';
  }
  const trimmed = segment.trim();
  if (!trimmed) {
    return '';
  }
  let lastNonMathIndex = -1;
  for (let index = 0; index < segment.length; index += 1) {
    if (!isMathTokenChar(segment[index])) {
      lastNonMathIndex = index;
    }
  }
  if (lastNonMathIndex < 0) {
    return trimmed;
  }
  for (let index = lastNonMathIndex + 1; index < segment.length; index += 1) {
    if (isMathTokenChar(segment[index])) {
      return segment.slice(index).trim();
    }
  }
  return trimmed;
};

const copyTextToClipboard = async (text) => {
  if (!text || typeof navigator === 'undefined') {
    return false;
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Fallback to legacy method.
    }
  }

  if (typeof document === 'undefined') {
    return false;
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    // Ignore clipboard errors.
  }
  return false;
};

const extractExpressionSegment = (value, equalIndex) => {
  if (equalIndex <= 0) {
    return '';
  }
  let start = equalIndex - 1;
  while (start >= 0 && EXPRESSION_CHAR_REGEX.test(value[start])) {
    start -= 1;
  }
  const segment = value.slice(start + 1, equalIndex).trim();
  return splitOnNumericWhitespace(segment);
};

const findExpressionResult = (segment) => {
  if (!segment) {
    return '';
  }
  for (let offset = 0; offset < segment.length; offset += 1) {
    const candidate = segment.slice(offset);
    if (candidate) {
      const result = getMathExpressionPreview(candidate);
      if (result) {
        return result;
      }
    }
  }
  return '';
};

export default function RHFInlineMathTextField({ name, onInput, helperText, ...other }) {
  const { control } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();
  const isApplyingRef = useRef(false);
  const lastValueRef = useRef('');

  const processEqualSign = (event, onChange) => {
    // 如果正在應用自動計算結果，直接更新表單值，不進行自動計算
    if (isApplyingRef.current) {
      onChange(event);
      return;
    }

    const inputElement = event.target;
    const value = inputElement.value || '';
    const cursor = inputElement.selectionStart ?? value.length;
    const equalIndex = cursor - 1;
    const previousValue = lastValueRef.current;
    lastValueRef.current = value;

    // 檢查游標前一個字元是否為等號（支援全形和半形）
    if (equalIndex < 0 || (value[equalIndex] !== '=' && value[equalIndex] !== '＝')) {
      // 如果不是等號，正常處理 onChange
      onChange(event);
      return;
    }
    
    // 如果前一個值在相同位置也是等號，則不處理（避免重複觸發）
    if (
      previousValue &&
      previousValue.length > equalIndex &&
      (previousValue[equalIndex] === '=' || previousValue[equalIndex] === '＝')
    ) {
      // 正常處理 onChange
      onChange(event);
      return;
    }

    // 提取等號前的表達式片段
    const segment = extractExpressionSegment(value, equalIndex);
    if (!segment) {
      // 正常處理 onChange
      onChange(event);
      return;
    }

    // 計算表達式結果
    const result = findExpressionResult(segment);
    if (!result) {
      // 正常處理 onChange
      onChange(event);
      return;
    }

    // 構建新值：等號前 + 等號 + 結果 + 等號後
    const before = value.slice(0, equalIndex + 1);
    const after = value.slice(equalIndex + 1);
    const nextValue = `${before}${result}${after}`;
    
    // 如果值沒有變化，則不處理
    if (nextValue === value) {
      onChange(event);
      return;
    }

    // 標記正在應用變更，避免遞迴觸發
    isApplyingRef.current = true;
    lastValueRef.current = nextValue;
    
    // 創建新的事件物件，使用計算後的值
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: nextValue,
      },
    };

    // 更新表單值
    onChange(syntheticEvent);
    const resultForCopy = result.replace(/,/g, '');
    copyTextToClipboard(resultForCopy).then((copied) => {
      if (copied) {
        enqueueSnackbar(`已複製: ${resultForCopy}`);
      }
    });

    // 在下一個動畫幀中設置游標位置
    requestAnimationFrame(() => {
      try {
        const nextCursor = before.length + result.length;
        inputElement.setSelectionRange(nextCursor, nextCursor);
      } catch (error) {
        // 忽略非文字輸入的選擇問題（例如 multiline TextField）
      }
      isApplyingRef.current = false;
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          value={typeof field.value === 'number' && field.value === 0 ? '' : field.value}
          error={!!error}
          helperText={error ? error?.message : helperText}
          onInput={onInput}
          onChange={(e) => {
            processEqualSign(e, field.onChange);
          }}
          {...other}
        />
      )}
    />
  );
}

RHFInlineMathTextField.propTypes = {
  name: PropTypes.string.isRequired,
  onInput: PropTypes.func,
  helperText: PropTypes.node,
};
